import { useState, useEffect, useContext, createContext } from 'react';
import { useAuth } from './useAuthSimple';
import { API_HOST } from '../config/api.config.js';
import logger from '@/utils/logger';
import { getToken } from '@/utils/storage';

// Context pour les feature flags
const FeatureFlagsContext = createContext();

/**
 * Provider pour les feature flags
 */
export const FeatureFlagsProvider = ({ children, subscription = 'basic' }) => {
  // Initialiser avec la config par défaut pour éviter l'état "tout grisé" au chargement
  const [config, setConfig] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(subscription);

  // Réagir à la connexion utilisateur : recharger la config quand l'utilisateur est disponible
  // (1ère connexion ou retour après refresh) pour avoir la bonne offre (Basic, etc.)
  const { user } = useAuth();

  // URL de base de l'API (alignée avec useAuth_simple.jsx)
  const API_BASE_URL = API_HOST;

  // Configuration par défaut (fallback) — alignée avec le backend et le document feature flags v1.0
  // OFFRES : 2 tiers uniquement — basic (IoT seul) | premium (IoT + app chauffeur)
  const getDefaultConfig = () => ({
    basic: {
      pages: ['students', 'parents', 'drivers', 'buses', 'routes', 'stops', 'gps-tracking', 'assignments', 'settings'],
      features: {
        iot_tracking: true,
        driver_app_access: false, // ❌ App chauffeur non disponible en Basic
        student_checkin: false, // ❌ Nécessite app chauffeur
        parent_requests: false, // ❌ Nécessite récepteur côté chauffeur
        freshness_indicator: true,
        students: { metrics: false, addModal: 'basic' },
        parents: { metrics: false, addModal: 'basic' },
        drivers: { metrics: false, addModal: 'basic' },
        buses: {
          metrics: false,
          addModal: 'basic',
          tabs: { maintenance: false },
        },
        routes: {
          metrics: false,
          addModal: 'basic',
          tabs: { horaires: false, optimisation: false, analytics: false },
        },
        stops: {
          metrics: false,
          addModal: 'basic',
          tabs: { equipements: false, gpsLocation: false, securite: false },
        },
        'gps-tracking': {
          metrics: false,
          realTimeTracking: true,
          historicalData: false,
          geofencing: true,
        },
        assignments: {
          metrics: false,
          mapInteraction: false,
          routeOptimization: false,
          realTimeSync: false,
        },
        settings: {
          profile: true,
          security: true,
          notifications: true,
          school: true,
        },
      },
    },
    premium: {
      pages: [
        'students',
        'parents',
        'drivers',
        'buses',
        'routes',
        'stops',
        'gps-tracking',
        'assignments',
        'dashboard',
        'notifications',
        'requests',
        'incidents',
        'settings',
      ],
      features: {
        iot_tracking: true,
        driver_app_access: true, // ✅ App chauffeur mobile activée
        student_checkin: true, // ✅ Pointage montée/descente
        parent_requests: true, // ✅ Déclaration absence + demandes
        freshness_indicator: true,
        trip_sharing: true,
        incident_notifications: true,
        students: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
        },
        parents: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
        },
        drivers: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
          assignments: true,
        },
        buses: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
          tabs: { maintenance: true },
        },
        routes: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
          tabs: { horaires: true, optimisation: true, analytics: true },
        },
        stops: {
          metrics: true,
          addModal: 'full',
          exportData: true,
          bulkActions: true,
          tabs: { equipements: true, gpsLocation: true, securite: true },
        },
        'gps-tracking': {
          metrics: true,
          realTimeTracking: true,
          historicalData: true,
          geofencing: true,
          alerts: true,
          reports: true,
          filters: 'advanced',
          playback: true,
        },
        assignments: {
          metrics: true,
          mapInteraction: true,
          routeOptimization: true,
          realTimeSync: true,
        },
        dashboard: {
          metrics: true,
          widgets: 'premium',
          customization: true,
          realTimeUpdates: true,
        },
        notifications: {
          customTemplates: true,
          history: true,
          targeting: true,
        },
        requests: {
          absenceDeclaration: true,
          stopChange: true,
          busChange: true,
        },
        settings: {
          profile: true,
          security: true,
          notifications: true,
          school: true,
          advanced: true,
          integrations: true,
          billing: true,
        },
      },
    },
  });

  // Initialiser config avec getDefaultConfig au montage pour éviter le null
  useEffect(() => {
    if (!config) {
      setConfig(getDefaultConfig());
    }
  }, []);

  // Charger la configuration depuis l'API
  const loadConfig = async () => {
    const token = getToken();
    if (!token) {
      // No valid token yet — keep default config and wait for user to log in
      if (!config) setConfig(getDefaultConfig());
      setIsLoaded(true);
      return;
    }

    try {
      // 1. Récupérer la configuration utilisateur (abonnement effectif)
      const userConfigResponse = await fetch(`${API_BASE_URL}/api/admin/features/user-config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (userConfigResponse.ok) {
        const userData = await userConfigResponse.json();
        if (userData.success && userData.data) {
          setCurrentSubscription(userData.data.effective_subscription);

          if (userData.data.config) {
            setConfig((prevConfig) => {
              const newConfig = { ...(prevConfig || getDefaultConfig()) };
              newConfig[userData.data.effective_subscription] = userData.data.config;
              return newConfig;
            });
          }
        }
      } else {
        logger.warn(`⚠️ [Features] Erreur API user-config: ${userConfigResponse.status}`);
      }

      // 2. Récupérer la matrice complète (optionnel si on a déjà ce qu'il faut)
      const configResponse = await fetch(`${API_BASE_URL}/api/admin/features/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        if (configData.success) {
          // logger.debug('🎯 [Features] Matrice complète chargée');
          setConfig(configData.data);
        }
      }

      setIsLoaded(true);
    } catch (error) {
      logger.error('❌ [Features] Erreur lors du chargement:', error);
      if (!config) setConfig(getDefaultConfig());
      const jwtPlan = user?.school?.subscription_plan;
      if (jwtPlan) setCurrentSubscription(jwtPlan);
      setIsLoaded(true);
    }
  };

  // Charger la config au montage uniquement si un token valide est présent
  // (évite les 401 "Bearer null" quand l'auth n'est pas encore initialisée)
  useEffect(() => {
    const raw = getToken();
    if (raw) {
      loadConfig();
    } else if (!config) {
      setConfig(getDefaultConfig());
      setIsLoaded(true);
    }
  }, []);

  // Au premier login : recharger la config dès que l'utilisateur est connecté
  // pour récupérer l'abonnement réel (Basic, etc.) et ne pas tout afficher en grisé
  useEffect(() => {
    if (user?.id) {
      loadConfig();
    }
  }, [user?.id]);

  // Fonctions utilitaires
  const isPageEnabled = (pageName) => {
    // Sécurité: si config pas chargée, on utilise le défaut
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return false;

    const pages = subConfig.pages || [];
    return pages.includes('*') || pages.includes(pageName);
  };

  const isFeatureEnabled = (page, feature) => {
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return false;

    const pageConfig = subConfig.features?.[page] || subConfig.features?.['*'];

    if (!pageConfig) return false;
    return pageConfig[feature] === true;
  };

  const isTabEnabled = (page, tabName) => {
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return false;

    const pageConfig = subConfig.features?.[page];

    if (!pageConfig || !pageConfig.tabs) return false;
    const tabs = pageConfig.tabs;
    return tabs === '*' || tabs[tabName] === true;
  };

  const getModalType = (page) => {
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return 'disabled';

    const pageConfig = subConfig.features?.[page];
    return pageConfig?.addModal || 'disabled';
  };

  const getSubscriptionLimits = () => {
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return {};
    return subConfig.limits || {};
  };

  const canPerformAction = (page, action) => {
    const activeConfig = config || getDefaultConfig();
    const subConfig = activeConfig[currentSubscription];

    if (!subConfig) return false;

    const pageConfig = subConfig.features?.[page];
    return pageConfig?.[action] === true;
  };

  const getRequiredSubscription = (page, feature) => {
    const activeConfig = config || getDefaultConfig();
    for (const sub of ['basic', 'premium']) {
      const pageConfig = activeConfig[sub]?.features?.[page];
      if (pageConfig?.[feature] === true) return sub;
    }
    return 'premium';
  };

  // Fonction helper pour vérifier une fonctionnalité (alias pour isFeatureEnabled)
  const hasFeature = (feature, page = null) => {
    if (page) {
      return isFeatureEnabled(page, feature);
    }
    // Si pas de page spécifiée, on cherche dans toutes les pages
    const pages = [
      'students',
      'parents',
      'drivers',
      'buses',
      'routes',
      'stops',
      'gps-tracking',
      'dashboard',
      'requests',
    ];
    return pages.some((p) => isFeatureEnabled(p, feature));
  };

  const value = {
    config: config || getDefaultConfig(),
    isLoaded,
    currentSubscription,
    setCurrentSubscription,
    isPageEnabled,
    isFeatureEnabled,
    isTabEnabled,
    getModalType,
    getSubscriptionLimits,
    canPerformAction,
    getRequiredSubscription,
    hasFeature,
    reloadConfig: loadConfig,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

/**
 * Hook pour utiliser les feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    throw new Error('useFeatureFlags doit être utilisé dans un FeatureFlagsProvider');
  }

  return context;
};

/**
 * Hook simplifié pour vérifier une fonctionnalité spécifique
 */
export const useFeature = (page, feature) => {
  const { isFeatureEnabled, isLoaded } = useFeatureFlags();

  return {
    isEnabled: isFeatureEnabled(page, feature), // Ne dépend plus de isLoaded pour éviter le flash
    isLoaded,
  };
};

/**
 * Hook pour vérifier l'accès à une page
 */
export const usePageAccess = (pageName) => {
  const { isPageEnabled, isLoaded } = useFeatureFlags();

  return {
    hasAccess: isPageEnabled(pageName), // Ne dépend plus de isLoaded
    isLoaded,
  };
};

/**
 * Hook pour les onglets
 */
export const useTabAccess = (page, tabName) => {
  const { isTabEnabled, isLoaded } = useFeatureFlags();

  return {
    hasAccess: isTabEnabled(page, tabName), // Ne dépend plus de isLoaded
    isLoaded,
  };
};
